// @flow
import React, { PureComponent } from 'react';
import { Redirect } from 'react-router-dom';
import { withHelmet } from 'infrastructure/seo/Helmet';
import { getPublicAssets } from 'utils/assetsUtils';
import Breadcrumb from 'components/fragments/Breadcrumb';
import NewsListElement from 'components/newsletter/NewsListElement';
import Pagination from 'components/newsletter/Pagination';
import type { ArticleType, CategoryType } from 'types/Article';
import { nullLoadingArticle } from 'types/Article';
import type { Status } from 'types/Status';
import type { State as SidebarType } from 'reducers/sidebarReducer';
import { STATUS_FAILURE, STATUS_LOADING, STATUS_DEFAULT, STATUS_UNKNOWN } from 'constants/statusConstants';
import { formatFullDate, getDateFromString } from 'utils/dateUtils';
import { ARTICLE_PER_PAGE } from 'constants/articleConstants';
import { Link } from 'react-router-dom';
import SideBar from 'components/sidebar/Sidebar';
import Loading from 'components/fragments/Loading';
import { getMetaBySlug } from 'utils/seoUtils';

export type OwnProps = {
  match: {
    params: {
      category: string
    }
  }
};

export type StateProps = {
  articles: ArticleType[],
  articleNumber: number,
  categories: CategoryType[],
  status: Status,
  sidebar: SidebarType,
  ligue: string
};

export type DispatchProps = {
  fetchArticlesCategory: (page: number, per_page: number, category: string) => void,
  fetchGenericSidebar: () => void
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  currentPage: number
};

class NewsletterCategory extends PureComponent<Props, State> {
  props: Props;
  state: State = {
    currentPage: 1
  };

  componentDidMount() {
    this.onPageChanged(1);
    this.props.fetchGenericSidebar();
  }

  onPageChanged = (currentPage: number) => {
    const { category } = this.props.match.params;
    this.props.fetchArticlesCategory(currentPage, ARTICLE_PER_PAGE, category);
    this.setState({ currentPage });
  };

  renderArticles = (articles: ArticleType[]): any => {
    return articles.map((article, index) => (
      <NewsListElement
        key={index}
        url={`/actualites/${article.label.slug}/${article.slug}`}
        img={article.illustration}
        title={article.title}
        date={article.date}
        description={article.excerpt ? article.excerpt : article.meta_description}
        caption={article.caption}
      />
    ));
  };

  renderSidebar = () => {
    const { sidebar } = this.props;
    return <SideBar sidebarContent={sidebar.data} status={sidebar.status} />;
  };

  render() {
    const { status } = this.props;
    if (status === STATUS_LOADING || status === STATUS_DEFAULT) {
      return <Loading />;
    }
    if (status === STATUS_FAILURE || status === STATUS_UNKNOWN) {
      return <Redirect to="/404" />;
    }

    const mainArticle: ArticleType = this.props.articles.length > 0 ? this.props.articles[0] : nullLoadingArticle;
    const articles: ArticleType[] = this.props.articles.slice(1, ARTICLE_PER_PAGE);
    const { category } = this.props.match.params;

    const categoryObject = this.props.categories.find(c => c.slug === category);
    const categoryName = categoryObject ? categoryObject.name : category;
    return (
      <React.Fragment>
        <section className="banner ">
          <div
            className="banner__thumbnail img-green"
            style={{ backgroundImage: `url(${getPublicAssets('/img/banner/hub-actualite.jpg')})` }}
          />
          <div className="container">
            <Breadcrumb
              elements={[{ name: 'Accueil', to: '/' }, { name: 'Actualités', to: '/actualites' }, categoryName]}
              isDark={true}
            />
            <h1 className="ft-h2">
              <span>Dernières actualités :</span> {categoryName}
            </h1>
          </div>
        </section>
        <div className="container">
          <div className="row">
            <div className="col col-md-8 col-lg-9 bg-overflow">
              <section>
                <div className="row">
                  <div className="col col-lg-8on9 banner-over">
                    <Link
                      to={`/actualites/${mainArticle.label.slug}/${mainArticle.slug}`}
                      className="news news--big mb-4 mb-md-2 mb-lg-5"
                    >
                      <article>
                        <div
                          className="news__thumbnail img-blue"
                          style={{ backgroundImage: `url(${mainArticle.illustration})` }}
                        />
                        <div>
                          <h3 className="news__title">{mainArticle.title}</h3>
                          <p className="ft-h6">
                            <span className="news__date">{formatFullDate(getDateFromString(mainArticle.date))}</span>
                          </p>
                        </div>
                      </article>
                    </Link>
                  </div>
                </div>
                <div className="row">
                  <div className="col col-lg-8on9">
                    {this.renderArticles(articles)}
                    <Pagination
                      current={this.state.currentPage}
                      minimum={1}
                      maximum={Math.ceil(this.props.articleNumber / ARTICLE_PER_PAGE)}
                      isMaximumLastPage={
                        this.state.currentPage >= Math.ceil(this.props.articleNumber / ARTICLE_PER_PAGE)
                      }
                      onPageChanged={this.onPageChanged}
                    />
                  </div>
                </div>
              </section>
            </div>
            {this.renderSidebar()}
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default withHelmet(NewsletterCategory)((Helmet, pageProps) => {
  const meta = getMetaBySlug(pageProps.location.pathname);
  return (
    <Helmet>
      <title>{meta.title(pageProps.ligue)}</title>
      <meta name={'description'} content={meta.description(pageProps.ligue)} />
      <meta property={'og:title'} content={meta.title(pageProps.ligue)} />
      <meta property={'og:description'} content={meta.description(pageProps.ligue)} />
      <link rel={'canonical'} href={window.location.href} />
    </Helmet>
  );
});
